import getConfig from 'next/config';

import * as Styled from '../../../../../../components/SearchPage/components/Listings/components/SearchCard/SearchCard.styled';
import type { SearchCardProps } from '../../../../../../components/SearchPage/components/Listings/components/SearchCard/SearchCard.typed';
import { Card } from '../../../../../../components/ToolkitV2/CardV2/Card';
import { ORIENTATION } from '../../../../../../components/ToolkitV2/CardV2/Card.typed';
import { BrandingStrip } from '../../../../../../components/ToolkitV2/CardV2/components/BrandingStrip/BrandingStrip';
import { Highlight } from '../../../../../../components/ToolkitV2/CardV2/components/Highlight/Highlight';
import { Gallery } from '../../../../../../components/AdCard/Gallery/Gallery';
import Price from '../../../../../../components/AdCard/Price/Price';
import { SaveAd } from '../../../../../../components/AdCard/SaveAd/SaveAd';
import { useSaveAd } from '../../../../../../components/Support/UseSaveAd/useSaveAd';
import { WithImageOverlay } from '../../../../../../components/ToolkitV2/CardV2/enhancements/WithImageOverlay/WithImageOverlay';
import { RatingStrip } from '../RatingStrip/RatingStrip';
import { PricingIndicatorIconText } from '../../../../../../components/PricingIndicator/PricingIndicatorIconText/PricingIndicatorIconText';

const SearchCard = (props: SearchCardProps) => {
  const {
    ad,
    isGrid = false,
    currentSection,
    createSavedAdEvent,
    deleteSavedAdEvent,
  } = props;
  const {
    gallery,
    dealer,
    seller,
    relativeUrl,
    friendlyUrl,
    spotlighted,
    title,
    tags,
    tagsV2,
    imageAlt,
    metaInfo,
    age,
    county,
    countyTown,
    section,
  } = ad;

  const {
    publicRuntimeConfig: { FEATURE_PRICING_INDICATOR },
  } = getConfig();
  const activePricingIndicatorState =
    FEATURE_PRICING_INDICATOR && section.name === 'cars'
      ? ad.priceIndicator?.state
      : undefined;

  const displayTags = tagsV2 ?? tags;

  const { previousPrice, price, financePPM, priceOnRequest } = ad.priceInfo;
  const listingUrl = relativeUrl ?? friendlyUrl;

  // TODO: Refactor this to check for a boolean for freeReport instead of inferring from tags
  // Not currently possible due to API response, planned refactor in the new year.
  // Ticket https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=board/4715599953694369603&appConfig=eyJhY2lkIjoiIn0=&boardPopup=userstory/112350/silent

  const isFreeHistoryCheckAd = tagsV2?.some(
    (tag) => tag.type === 'freeHistoryReport',
  );

  const orientationConfig = {
    orientationSmall: ORIENTATION.VERTICAL,
    orientationMedium: isGrid ? ORIENTATION.VERTICAL : ORIENTATION.HORIZONTAL,
    orientationLarge: isGrid ? ORIENTATION.VERTICAL : ORIENTATION.HORIZONTAL,
  };

  // Todo: Remove when BE release their change
  const getLocationText = () => {
    const countyName = county?.replace(/^Co\. /, '');
    const showCountyTown = !isGrid && section.name !== 'cars';
    const locationText =
      countyTown && countyName && showCountyTown
        ? `${countyTown}, ${countyName}`
        : countyName;
    const countyOrCountyTownText =
      isGrid && countyName ? countyName : locationText;
    return countyOrCountyTownText ? [countyOrCountyTownText] : [];
  };

  const showAge = isGrid || section.name !== 'cars';

  const metaData: string[] = [
    ...(!isGrid && metaInfo ? metaInfo : []),
    ...(showAge ? [age] : []),
    ...getLocationText(),
    ...(currentSection !== section.name ? [section.displayName] : []),
  ];

  const { onSave, isAdSaved, isSaving } = useSaveAd({
    adId: ad.id,
    userSaved: ad.saved,
    section: ad.section.name,
    sellerType: dealer ? 'DEALER' : 'PRIVATE',
    sectionName: ad.section.name,
    trackCreateSaveAd: createSavedAdEvent,
    trackDeleteSaveAd: deleteSavedAdEvent,
    isFreeHistoryCheckAd,
    category: ad.section.name,
    location: ad.county,
    price: ad.priceInfo.price,
  });

  return (
    // We chose not to use Next Link here as explained in this PR:
    // https://github.com/distilledmedia/dsch-donedeal-frontend/pull/2183
    <a href={listingUrl}>
      <Styled.Wrapper isGrid={isGrid}>
        {!isGrid && dealer && (
          <BrandingStrip
            {...dealer}
            logo={dealer.logo?.small}
            isGrid={isGrid}
          />
        )}
        {spotlighted && <Highlight color="GREY_DARKER">Spotlight</Highlight>}
        <Card
          {...orientationConfig}
          image={
            <WithImageOverlay
              topRightSlot={
                <Styled.ImageSavedAd isGrid={isGrid}>
                  <SaveAd
                    toggleSave={onSave}
                    isAdSaved={isAdSaved}
                    hideText
                    iconSizeDesktop="MD"
                    isSaving={isSaving}
                  />
                </Styled.ImageSavedAd>
              }
            >
              <Gallery
                coverImage={gallery?.coverImage.small2}
                thumbnails={gallery?.thumbnails}
                totalMediaCount={gallery?.totalMediaCount}
                isSpotlight={ad.spotlighted}
                isGrid={isGrid}
                title={title}
                imageAlt={imageAlt ?? 'no-image'}
              />
            </WithImageOverlay>
          }
          header={
            <Styled.HeaderContainer>
              <Styled.Header isGrid={isGrid}>
                <RatingStrip
                  dealer={dealer || null}
                  seller={seller || null}
                  isGrid={isGrid}
                />
                <Styled.TitleMetaContainer>
                  <Styled.Title
                    lines={2}
                    isGrid={isGrid}
                    fontSizes={{
                      sm: isGrid ? 'M14' : 'L18',
                      md: isGrid ? 'M16' : 'L18',
                    }}
                    forwardedAs={'p'}
                  >
                    {title}
                  </Styled.Title>
                  <Styled.MetaInfo lines={2} forwardedAs={'ul'}>
                    {metaData &&
                      metaData.map((info, index) => (
                        <Styled.MetaInfoItem
                          key={index}
                          fontSizes={{
                            sm: 'M14',
                          }}
                        >
                          {info}
                        </Styled.MetaInfoItem>
                      ))}
                  </Styled.MetaInfo>
                </Styled.TitleMetaContainer>
              </Styled.Header>

              {!isGrid && displayTags?.length && (
                <Styled.TagsList tags={displayTags} />
              )}
            </Styled.HeaderContainer>
          }
          footer={
            <>
              <Price
                previousPrice={previousPrice}
                price={price}
                financePPM={financePPM}
                priceOnRequest={priceOnRequest}
                isGrid={isGrid}
                pricingIndicatorSlot={
                  activePricingIndicatorState ? (
                    <PricingIndicatorIconText
                      status={activePricingIndicatorState}
                      textSize="M14"
                    />
                  ) : undefined
                }
              />
              {!isGrid && (
                <Styled.BodySavedAd isGrid={isGrid}>
                  <SaveAd
                    toggleSave={onSave}
                    isAdSaved={isAdSaved}
                    hideText
                    iconSizeDesktop="MD"
                    isSaving={isSaving}
                  />
                </Styled.BodySavedAd>
              )}
            </>
          }
          gapSmall={isGrid ? 'S4' : 'S8'}
          gapMedium={isGrid ? 'S4' : undefined}
          paddingSmall={isGrid ? 'S4' : 'M16'}
          paddingMedium={isGrid ? 'S8' : 'M16'}
          footerAlignment="center"
        />
      </Styled.Wrapper>
    </a>
  );
};

export { SearchCard };
